import { useEffect, useRef } from 'react';
import { isEqual } from 'radash';
import type { FindingDetailDto } from '@terra/shared-contracts-findings';
import type { EditableFields } from '@terra/ui-design-system';

type CvssScoreFinding = Pick<
  FindingDetailDto,
  'id' | 'cvssScoreStatus' | 'severity' | 'cvss' | 'cvss40' | 'cvss31'
>;

const withUneditedScoreFields = ({
  previous,
  baseline,
  finding,
}: {
  previous: EditableFields;
  baseline: EditableFields;
  finding: CvssScoreFinding;
}): EditableFields => {
  const next = { ...previous };
  let changed = false;
  const severity = finding.severity ?? previous.severity;
  const cvss = finding.cvss ?? null;
  const cvss40 = finding.cvss40 ?? null;
  const cvss31 = finding.cvss31 ?? null;

  if (
    isEqual(previous.severity, baseline.severity) &&
    !isEqual(previous.severity, severity)
  ) {
    next.severity = severity;
    changed = true;
  }

  if (isEqual(previous.cvss, baseline.cvss) && !isEqual(previous.cvss, cvss)) {
    next.cvss = cvss;
    changed = true;
  }

  if (
    isEqual(previous.cvss40, baseline.cvss40) &&
    !isEqual(previous.cvss40, cvss40)
  ) {
    next.cvss40 = cvss40;
    changed = true;
  }

  if (
    isEqual(previous.cvss31, baseline.cvss31) &&
    !isEqual(previous.cvss31, cvss31)
  ) {
    next.cvss31 = cvss31;
    changed = true;
  }

  return changed ? next : previous;
};

export function useSyncCvssScoreOnSettle({
  finding,
  baselineFindingId,
  originalFields,
  setEditableFields,
  setOriginalFields,
}: {
  finding: CvssScoreFinding | null | undefined;
  baselineFindingId: string | null;
  originalFields: EditableFields | null;
  setEditableFields: (
    update: (previous: EditableFields) => EditableFields,
  ) => void;
  setOriginalFields: (
    update: (previous: EditableFields | null) => EditableFields | null,
  ) => void;
}): void {
  const pendingFindingIds = useRef(new Set<string>());

  useEffect(() => {
    if (!finding) {
      return;
    }

    if (finding.cvssScoreStatus === 'PENDING') {
      pendingFindingIds.current.add(finding.id);

      return;
    }

    if (
      !pendingFindingIds.current.has(finding.id) ||
      !originalFields ||
      baselineFindingId !== finding.id
    ) {
      return;
    }

    pendingFindingIds.current.delete(finding.id);
    const baseline = originalFields;

    setEditableFields((previous) =>
      withUneditedScoreFields({ previous, baseline, finding }),
    );
    setOriginalFields((previous) =>
      previous
        ? withUneditedScoreFields({
            previous,
            baseline,
            finding,
          })
        : previous,
    );
  }, [
    baselineFindingId,
    finding,
    originalFields,
    setEditableFields,
    setOriginalFields,
  ]);
}
